// Third Party
import React from 'react';
import classNames from 'classnames';
import throttle from 'lodash/throttle';

// Components
import IncomeStreamComparisonScenario from './IncomeStreamComparisonScenario';
import IncomeStreamForm from './IncomeStreamForm';
import ModalV2 from '@/views/components/modals/ModalV2';
import { CancelButton, PrimaryButton } from '@/views/components/buttons';

// Utils
import { COMPARISON_CARD_WIDTH, COMPARISON_CARD_WIDTH_SMALL } from '../../../../utils/constants';
import { EDIT_SCENARIO_CARD_WIDTH } from '../../../../utils/income-stream';
import ComparisonItem from '@/types/comparison-item';
import IncomeStreamScenario from '@/types/income-stream-scenario';
import Portfolio from '@/types/portfolio';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import Comparison from '@/types/comparison';
import { formatMessage } from '@/utils/i18n';
import { PHONE_BREAKPOINT } from '@/hooks/useScreenSize';

type DispatchProps = {
  createNewScenario: (item: ComparisonItem) => void;
  setIncomeStreamCapital: (item: ComparisonItem, capital: number, startDate: Date, endDate: Date) => void;
  saveScenario: (item: ComparisonItem, startDate: Date) => void;
  removeScenario: (item: ComparisonItem, scenario: IncomeStreamScenario) => void;
  loadScenarioForEdit: (scenario: IncomeStreamScenario) => void;
  cancelScenarioEditing: (item: ComparisonItem) => void;
  updateScenario: (scenario: IncomeStreamScenario) => void;
  onSavePortfolio: (p: Portfolio) => Promise<Portfolio>;
  onItemRemove: (item: ComparisonItem) => void;
  onModifyPortfolio: (item: ComparisonItem) => void;
  onClonePortfolio: (item: ComparisonItem) => void;
  updateComparisonItemColor: (item: ComparisonItem, color: string) => void;
};

type Props = DispatchProps & {
  item: ComparisonItem;
  years: string[];
  editedScenario?: IncomeStreamScenario;
  startDate: Date;
  endDate: Date;
  editedPortfolio: Portfolio;
  readOnly?: boolean;
  editMode: boolean;
  user?: Nullable<User>;
  isEditPinned: boolean;
  isEdited: boolean;
  comparison: Comparison;
  popoverContainerRef: Nullable<HTMLDivElement>;
  scrollbarContainerRef?: HTMLElement;
};

type State = {
  cardWidth: number;
};

class IncomeStreamCard extends React.Component<Props> {
  readonly state: State = {
    cardWidth: 0,
  };

  handleResize = throttle(() => {
    this.adjustCardWidth();
  }, 20);

  componentDidMount() {
    const { editedPortfolio } = this.props;

    window.addEventListener('resize', this.handleResize);

    if (editedPortfolio._id) {
      this.focusPortfolioEdit();
    }

    this.adjustCardWidth();
  }

  componentDidUpdate(prevProps: Props) {
    const { item, editedScenario, editedPortfolio } = this.props;

    const scenariosLengthChanged =
      prevProps.item.scenarios &&
      prevProps.item.scenarios.incomeStreamDistribution &&
      item.scenarios &&
      item.scenarios.incomeStreamDistribution &&
      prevProps.item.scenarios.incomeStreamDistribution.length !== item.scenarios.incomeStreamDistribution.length;

    const scenarioBeingEdited = prevProps.editedScenario !== editedScenario;

    if (scenariosLengthChanged || scenarioBeingEdited) {
      this.adjustCardWidth();
    }

    if (editedPortfolio._id && prevProps.editedPortfolio._id !== editedPortfolio._id) {
      this.focusPortfolioEdit();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  adjustCardWidth = () => {
    const { item, isEditPinned } = this.props;

    // Must calc width for income stream cards based on dynamic num of scenarios. Used for the transition state when editting a portfolio.
    const windowWidth = isEditPinned ? window.innerWidth - 480 : window.innerWidth;

    // 1100 based on breakpoint in css for normal sized card width to a small card width
    const cardWidth = windowWidth < 1100 ? COMPARISON_CARD_WIDTH_SMALL : COMPARISON_CARD_WIDTH;

    if (!item.scenarios || !item.scenarios.incomeStreamDistribution) {
      return this.setState({ cardWidth });
    }

    const width =
      item.scenarios &&
      item.scenarios.incomeStreamDistribution.reduce((acc, curr) => {
        if (this.scenarioIsBeingEdited(curr)) {
          return acc + EDIT_SCENARIO_CARD_WIDTH;
        }

        return acc + cardWidth;
      }, cardWidth);

    return this.setState({ cardWidth: width });
  };

  focusPortfolioEdit = () => {
    const cardsContainer = document.getElementsByClassName('income-stream-cards')[0] as HTMLElement;
    const target = document.getElementsByClassName('income-stream-card editing-mode')[0] as HTMLElement;

    if (cardsContainer && target && cardsContainer.scrollTo) {
      setTimeout(() => {
        cardsContainer.scrollTo({
          left: target.offsetLeft - COMPARISON_CARD_WIDTH,
          behavior: 'smooth',
        });
      }, 100);
    }
  };

  scenarioIsBeingEdited = (scenario: IncomeStreamScenario) => !!this.props.editedScenario && this.props.editedScenario.id === scenario.id;

  handleAddScenario = () => {
    const { item } = this.props;

    this.props.createNewScenario(item);
  };

  handleRemoveScenario = (scenario?: IncomeStreamScenario) => {
    const { item } = this.props;

    if (!scenario) {
      return;
    }

    this.props.removeScenario(item, scenario);
  };

  handleSaveScenario = () => {
    const { startDate, item } = this.props;
    this.props.saveScenario(item, startDate);
  };

  handleChangeCapital = (capital: number) => {
    const { startDate, endDate, item } = this.props;
    this.props.setIncomeStreamCapital(item, capital, startDate, endDate);
  };

  handleCancelScenarioEditing = () => {
    const { item } = this.props;

    this.props.cancelScenarioEditing(item);
  };

  renderDistributions = () => {
    const {
      item,
      item: { scenarios },
      years,
      editedScenario,
      user,
      readOnly,
      isEdited,
      comparison,
      popoverContainerRef,
      scrollbarContainerRef,
    } = this.props;

    if (!scenarios || !scenarios.incomeStreamDistribution) {
      return null;
    }

    return scenarios.incomeStreamDistribution.map((scenario, i) => {
      const scenarioIsEdited = this.scenarioIsBeingEdited(scenario);
      const scenarioNumber = i + 1;
      const isMobile = window.innerWidth <= PHONE_BREAKPOINT;

      if (scenarioIsEdited && editedScenario) {
        return (
          <React.Fragment key={scenario.id}>
            {isMobile ? (
              <ModalV2
                modalClassName="income-stream-edit-scenario-modal"
                show
                onClose={this.handleCancelScenarioEditing}
                title={formatMessage('Comparison.IncomeStream.AddScenario')}
                body={
                  <IncomeStreamForm
                    isMobile={isMobile}
                    item={item}
                    scenario={editedScenario}
                    updateScenario={this.props.updateScenario}
                    scenarioNumber={scenarioNumber}
                    handleSaveScenario={this.handleSaveScenario}
                    handleCancelScenarioEditing={this.handleCancelScenarioEditing}
                  />
                }
                footer={
                  <>
                    <CancelButton onClick={this.handleCancelScenarioEditing} />
                    <PrimaryButton onClick={this.handleSaveScenario}>Apply</PrimaryButton>
                  </>
                }
              />
            ) : (
              <div className="distribution-scenario editing-mode">
                <IncomeStreamForm
                  item={item}
                  scenario={editedScenario}
                  updateScenario={this.props.updateScenario}
                  scenarioNumber={scenarioNumber}
                  handleSaveScenario={this.handleSaveScenario}
                  handleCancelScenarioEditing={this.handleCancelScenarioEditing}
                />
              </div>
            )}
          </React.Fragment>
        );
      }

      return (
        <IncomeStreamComparisonScenario
          key={scenario.id}
          user={user}
          className="distribution-scenario"
          scenario={scenario}
          scenarioNumber={scenarioNumber}
          years={years}
          item={item}
          handleRemoveScenario={this.handleRemoveScenario}
          loadScenarioForEdit={this.props.loadScenarioForEdit}
          readOnly={readOnly}
          isEdited={isEdited}
          comparison={comparison}
          updateComparisonItemColor={this.props.updateComparisonItemColor}
          popoverContainerRef={popoverContainerRef}
          scrollbarContainerRef={scrollbarContainerRef}
        />
      );
    });
  };

  render() {
    const { item, years, readOnly, user, editMode, isEdited, comparison, popoverContainerRef, scrollbarContainerRef } = this.props;
    const { cardWidth } = this.state;

    const showEditPortfolio = editMode && !readOnly;

    const cardClasses = classNames('income-stream-card', {
      'editing-mode': isEdited,
      shared: !!item.providedBy,
    });

    const styles = !showEditPortfolio
      ? {
          width: cardWidth,
          transition: 'unset',
        }
      : {};

    return (
      <div className={cardClasses} key={`${item._id}`} style={styles} id={`card-${item._id}`}>
        <IncomeStreamComparisonScenario
          className="base-scenario"
          isBaseScenario
          scenario={item.scenarios && item.scenarios.defaultIncomeStream}
          user={user}
          years={years}
          item={item}
          handleChangeCapital={this.handleChangeCapital}
          handleAddScenario={this.handleAddScenario}
          onModifyPortfolio={this.props.onModifyPortfolio}
          onClonePortfolio={this.props.onClonePortfolio}
          onItemRemove={this.props.onItemRemove}
          readOnly={readOnly}
          isEdited={isEdited}
          comparison={comparison}
          updateComparisonItemColor={this.props.updateComparisonItemColor}
          popoverContainerRef={popoverContainerRef}
          scrollbarContainerRef={scrollbarContainerRef}
        />

        {this.renderDistributions()}
      </div>
    );
  }
}

export default IncomeStreamCard;
